import type { ResponseData } from '@vrplatform/data';
import type { paidStatus_enum, party_enum } from '@vrplatform/graphql';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import type { ExpenseFormQueryData } from './useExpenseFormQuery';

type RecurringPattern =
  ResponseData<'get:/transactions/recurring-templates/{id}'>['pattern']['frequency'];
type TransactionAttachment =
  ResponseData<'get:/transactions/{id}'>['attachments'][number];

export type ExpenseAttachmentDraft = {
  tempId: string;
  file: File;
  filename: string;
  isOwnerAccessible: boolean;
};

export type ExpenseAttachment = TransactionAttachment | ExpenseAttachmentDraft;

export const isExpenseAttachmentDraft = (
  attachment: ExpenseAttachment
): attachment is ExpenseAttachmentDraft => 'file' in attachment;

type AdditionalContext = {
  lock: ExpenseFormQueryData['lock'];
};

const context = createContext<AdditionalContext>({
  lock: undefined,
});

export type ExpenseFormInputs = {
  id: string | null;
  date: string;
  endDate: string | null;
  pattern: {
    frequency: RecurringPattern;
    interval: number;
  } | null;
  accountId: (string & {}) | 'non-trust';
  description: string;
  contactId: string | null;
  uniqueRef: string;
  paidStatus: paidStatus_enum;
  paidAt: string | null;
  attachments: ExpenseAttachment[];
  lines: {
    id: string | undefined;
    accountId: string | null;
    locked: string | boolean;
    party: party_enum;
    listingId: string | null;
    reservationId: string | null;
    description: string;
    centTotal: number;
    taxId: string | null;
    markup: number | undefined;
    markupTaxId: string | null;
    // markupId: string | null;
    // markupTaxRateId: string | null;
    // markupTaxBehavior: gqlV2.taxBehavior_enum | null;
    // markupType: 'rate' | 'cent';
  }[];
};

const useAdditionalContext = () => {
  return useContext(context);
};

export const useExpenseForm = () => {
  const m = useFormContext<ExpenseFormInputs>();
  const { lock } = useAdditionalContext();

  return useMemo(
    () => ({
      ...m,
      context: {
        lock,
      },
    }),
    [m, lock]
  );
};

export const useExpenseFormProvider = (values: ExpenseFormInputs | undefined) =>
  useForm<ExpenseFormInputs>({
    values,
    defaultValues: {
      paidStatus: 'paid',
      attachments: [],
      lines: [],
    },
  });

export const ExpenseFormProvider = ({
  children,
  lock,
  ...methods
}: {
  children: ReactNode;
  lock: ExpenseFormQueryData['lock'];
} & ReturnType<typeof useExpenseFormProvider>) => {
  const value = useMemo<AdditionalContext>(
    () => ({
      lock,
    }),
    [lock]
  );

  return (
    <FormProvider {...methods}>
      <context.Provider value={value}>{children}</context.Provider>
    </FormProvider>
  );
};
